import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { supabase } from './supabase';
import { useAuth } from './AuthContext';

// Single entitlement source of truth for the whole app. Reads
// GET /api/subscription — the same endpoint the publish gate uses —
// so the badge, the /pricing CTA state, and the gate all agree.
//
// Design notes:
// - Keyed on `user.id` (primitive), NOT the whole `user` object,
//   so Supabase's hourly TOKEN_REFRESHED event doesn't refetch.
// - Refetch on manual `refresh()` (used by CheckoutSuccessHandler and
//   after publish attempts).
// - When logged out, returns `{plan: null, status: null, loading: false}`.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubscriptionContext = createContext({
  plan: null,
  status: null,
  canPublish: false,
  loading: false,
  refresh: async () => null,
});

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const userId = user?.id || null;
  const [sub, setSub] = useState({ plan: null, status: null, canPublish: false });
  const [loading, setLoading] = useState(false);
  // Guard against a stale in-flight fetch overwriting fresher state
  // (e.g. user signs out mid-request).
  const activeUserRef = useRef(null);

  const fetchSubscription = useCallback(async ({ force = false } = {}) => {
    if (!userId) {
      setSub({ plan: null, status: null, canPublish: false });
      return null;
    }
    activeUserRef.current = userId;
    setLoading(true);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token;
      if (!token) return null;
      const url = force ? `${API}/subscription/refresh` : `${API}/subscription`;
      const method = force ? 'post' : 'get';
      const { data } = await axios({
        method,
        url,
        data: force ? {} : undefined,
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
      });
      if (activeUserRef.current !== userId) return null; // stale
      const next = {
        plan: data?.plan || 'free',
        status: data?.status || 'none',
        canPublish: !!data?.can_publish,
      };
      setSub(next);
      return next;
    } catch {
      return null;
    } finally {
      if (activeUserRef.current === userId) setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const refresh = useCallback(() => fetchSubscription({ force: true }), [fetchSubscription]);

  return (
    <SubscriptionContext.Provider value={{ ...sub, loading, refresh }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => useContext(SubscriptionContext);
